import React, { useState, useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { generateCertificateRef } from '../../../utils/certificateUtils';

const AdminView = () => {
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [pendingCerts, setPendingCerts] = useState([
    { id: 1, user_name: "Sarah Jenkins", org: "Exemplar Care", programme: "Mental Health Excellence", progress: 100 },
    { id: 2, user_name: "James Cole", org: "Exemplar Care", programme: "Safeguarding L3", progress: 95 }
  ]);

  const [newOrg, setNewOrg] = useState({
    name: '',
    type: 'Care Home',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    staff_count: 0,
    status: 'Active'
  });

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await supabase
        .from('organisations_1741860000000')
        .insert([newOrg])
        .select();

      if (error) throw error;
      
      setOrganisations([...organisations, data[0]]);
      setIsOrgModalOpen(false);
      setNewOrg({ name: '', type: 'Care Home', contact_name: '', contact_email: '', contact_phone: '', staff_count: 0, status: 'Active' });
      alert("Organisation created successfully!");
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const issueCertificate = (cert) => {
    const ref = generateCertificateRef(cert.org);
    // In a real app, this would call Supabase to create the record
    alert(`Certificate Issued!\nRef: ${ref}\nRecipient: ${cert.user_name}`);
    setPendingCerts(pendingCerts.filter(c => c.id !== cert.id));
  };

  return (
    <div className="space-y-8">
      {/* Header with KPI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Orgs', val: '12', icon: 'Briefcase', color: 'text-blue-600' },
          { label: 'Total Users', val: '458', icon: 'Users', color: 'text-navy-900' },
          { label: 'Certs Issued', val: '1,204', icon: 'Award', color: 'text-gold-600' },
          { label: 'Revenue (YTD)', val: '£84k', icon: 'TrendingUp', color: 'text-green-600' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{stat.label}</span>
              <SafeIcon name={stat.icon} className={`w-5 h-5 ${stat.color}`} />
            </div>
            <p className="text-2xl font-bold text-navy-900">{stat.val}</p>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center bg-navy-900 p-8 rounded-2xl text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="relative z-10">
          <h2 className="text-2xl font-serif font-bold mb-1">Administrative Console</h2>
          <p className="text-slate-400 text-sm">System-wide management of the Kajidori ecosystem.</p>
        </div>
        <button 
          onClick={() => setIsOrgModalOpen(true)}
          className="bg-gold-500 text-navy-900 px-8 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-gold-400 transform transition-all active:scale-95 shadow-lg relative z-10"
        >
          <SafeIcon name="Plus" className="w-5 h-5" /> New Organisation
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Certificate Automation Queue */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-navy-900 flex items-center gap-2">
              <SafeIcon name="Award" className="text-gold-500" /> Certificate Issuance Queue
            </h3>
            <span className="bg-navy-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-tighter">Automation Active</span>
          </div>
          <div className="divide-y divide-slate-100">
            {pendingCerts.map((cert) => (
              <div key={cert.id} className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cert.progress === 100 ? 'bg-green-100' : 'bg-slate-100'}`}>
                    <SafeIcon name="User" className={cert.progress === 100 ? 'text-green-600' : 'text-slate-400'} />
                  </div>
                  <div>
                    <p className="font-bold text-navy-900">{cert.user_name}</p>
                    <p className="text-xs text-slate-500">{cert.programme} • {cert.org}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className={`text-sm font-bold ${cert.progress === 100 ? 'text-green-600' : 'text-slate-400'}`}>{cert.progress}%</p>
                    <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Progress</p>
                  </div>
                  <button 
                    disabled={cert.progress < 100}
                    onClick={() => issueCertificate(cert)}
                    className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${
                      cert.progress === 100 
                      ? 'bg-navy-900 text-white hover:bg-navy-800 shadow-md' 
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Issue Certificate
                  </button>
                </div>
              </div>
            ))}
            {pendingCerts.length === 0 && (
              <div className="p-12 text-center text-slate-400 italic">No pending certificates in queue.</div>
            )}
          </div>
        </div>

        {/* System Health */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-navy-900 mb-6">Quick Actions</h3>
            <div className="space-y-3">
              {[
                { label: 'Bulk User Upload', icon: 'Upload' },
                { label: 'Course Management', icon: 'Book' },
                { label: 'Global Announcement', icon: 'Mail' },
                { label: 'System Audit Log', icon: 'Activity' }
              ].map((action, i) => (
                <button key={i} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-navy-900 hover:bg-slate-50 transition-all text-left group">
                  <div className="w-8 h-8 rounded bg-slate-50 flex items-center justify-center group-hover:bg-white border border-slate-100">
                    <SafeIcon name={action.icon} className="text-slate-400 group-hover:text-navy-900 w-4 h-4" />
                  </div>
                  <span className="text-sm font-semibold text-slate-600 group-hover:text-navy-900">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* New Organisation Modal */}
      {isOrgModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 animate-in fade-in zoom-in duration-200">
            <div className="bg-navy-900 p-6 text-white flex justify-between items-center">
              <div>
                <h3 className="text-xl font-serif font-bold">Register New Organisation</h3>
                <p className="text-slate-400 text-xs mt-1 font-medium">Onboard a new client to the collective.</p>
              </div>
              <button onClick={() => setIsOrgModalOpen(false)} className="bg-white/10 p-2 rounded-lg hover:bg-white/20 transition-colors">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateOrg} className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-navy-900 uppercase">Organisation Name *</label>
                  <input 
                    type="text" required
                    value={newOrg.name}
                    onChange={(e) => setNewOrg({...newOrg, name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-navy-900 outline-none transition-all" 
                    placeholder="e.g. Exemplar Health Group"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-navy-900 uppercase">Organisation Type</label>
                  <select 
                    value={newOrg.type}
                    onChange={(e) => setNewOrg({...newOrg, type: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 outline-none"
                  >
                    <option>Care Home</option>
                    <option>Supported Living</option>
                    <option>Domiciliary Care</option>
                    <option>NHS Trust</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-navy-900 uppercase">Primary Contact Name *</label>
                  <input 
                    type="text" required
                    value={newOrg.contact_name}
                    onChange={(e) => setNewOrg({...newOrg, contact_name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-navy-900 outline-none" 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-navy-900 uppercase">Primary Contact Email *</label>
                  <input 
                    type="email" required
                    value={newOrg.contact_email}
                    onChange={(e) => setNewOrg({...newOrg, contact_email: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-navy-900 outline-none" 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-navy-900 uppercase">Estimated Staff Count</label>
                  <input 
                    type="number"
                    value={newOrg.staff_count}
                    onChange={(e) => setNewOrg({...newOrg, staff_count: parseInt(e.target.value)})}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 outline-none" 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-navy-900 uppercase">Initial Status</label>
                  <select 
                    value={newOrg.status}
                    onChange={(e) => setNewOrg({...newOrg, status: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 outline-none"
                  >
                    <option>Active</option>
                    <option>Onboarding</option>
                    <option>Suspended</option>
                  </select>
                </div>
              </div>

              <div className="mt-10 flex gap-4">
                <button 
                  type="button"
                  onClick={() => setIsOrgModalOpen(false)}
                  className="flex-1 px-6 py-3 rounded-xl border border-slate-200 font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-6 py-3 rounded-xl bg-navy-900 text-white font-bold hover:bg-navy-800 shadow-lg transition-all active:scale-[0.98]"
                >
                  Create Organisation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminView;